import React, {useContext} from "react";
import {Chip} from "@mui/material";
import AutorenewIcon from "@mui/icons-material/Autorenew";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import {palette} from "@/constants";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {LocaleContext} from "@/app2/shared/contexts";

// Mini component for the optional chip
interface CourseChipProps {
	minutes?: number;
	progressPercentage?: number;
	watchedDurationSeconds?: number;
	remainingDurationSeconds?: number;
	userEnrollmentType?: string;
	totalDurationSeconds?: number;
	courseProgress?: number;
}

const CourseChip: React.FC<CourseChipProps> = ({
	minutes,
	progressPercentage,
	watchedDurationSeconds,
	remainingDurationSeconds,
	userEnrollmentType,
	totalDurationSeconds,
	courseProgress
}) => {
	const {t} = useSSTranslation();
	const [localeState] = useContext(LocaleContext);
	const isSpanish = localeState.locale === "es-419";

	// Determine chip type based on watched duration and remaining duration
	const getChipType = () => {
		// If user is enrolled and completed the course (no remaining time), show success
		if (userEnrollmentType && ((remainingDurationSeconds === 0 && watchedDurationSeconds && watchedDurationSeconds > 0) || courseProgress === 100)) {
			return "success";
		}

		// If user is enrolled (regardless of whether they've started), show warning
		if (userEnrollmentType) {
			return "warning";
		}

		// If not enrolled, show empty
		return "empty";
	};

	// Use the new logic based on duration fields
	const actualType = getChipType();

	const getChipLabel = () => {
		if (actualType === "success") {
			return isSpanish ? "Completado" : "Completed";
		}

		if (actualType === "warning") {
			// User is enrolled - show remaining time
			if (remainingDurationSeconds && remainingDurationSeconds > 0) {
				const hours = Math.floor(remainingDurationSeconds / 3600);
				const remainingMinutes = Math.floor((remainingDurationSeconds % 3600) / 60);
				let timeText = "";

				if (hours > 0) {
					timeText = `${hours} hr ${remainingMinutes} min`;
				} else {
					timeText = `${remainingMinutes} min`;
				}

				return isSpanish ? `Faltan ${timeText}` : `${timeText} left`;
			}

			// If no remaining time but user is enrolled, show total course duration
			const totalDuration = (watchedDurationSeconds || 0) + (remainingDurationSeconds || 0);
			if (totalDuration > 0) {
				const hours = Math.floor(totalDuration / 3600);
				const remainingMinutes = Math.floor((totalDuration % 3600) / 60);

				if (hours > 0) {
					return `${hours} hr ${remainingMinutes} min`;
				} else {
					return `${remainingMinutes} min`;
				}
			}

			// Fallback to progress percentage if no duration available
			if (progressPercentage !== undefined && progressPercentage !== null) {
				return `${progressPercentage}%`;
			}

			return isSpanish ? "En Progreso" : "In Progress";
		}

		// For empty type (not enrolled), show total course duration
		if (actualType === "empty") {
			// Use totalDurationSeconds if available, otherwise calculate from watched + remaining
			const totalDuration = totalDurationSeconds !== undefined ? totalDurationSeconds : (watchedDurationSeconds || 0) + (remainingDurationSeconds || 0);

			// Handle special case: if totalDuration is exactly 0, show 0 min
			if (totalDuration === 0) {
				return "0 min";
			}

			// If duration is 1-59 seconds, show as 1 minute
			if (totalDuration > 0 && totalDuration < 60) {
				return "1 min";
			}

			if (totalDuration > 0) {
				const hours = Math.floor(totalDuration / 3600);
				const remainingMinutes = Math.floor((totalDuration % 3600) / 60);

				if (hours > 0) {
					return `${hours} hr ${remainingMinutes} min`;
				} else {
					return `${remainingMinutes} min`;
				}
			}
			// Fall back to minutes prop if available
			if (minutes && minutes > 0) {
				const hours = Math.floor(minutes / 60);
				const remainingMinutes = minutes % 60;

				if (hours > 0) {
					return `${hours} hr ${remainingMinutes} min`;
				} else {
					return `${remainingMinutes} min`;
				}
			}
		}

		return "";
	};

	const hasContent = (watchedDurationSeconds && watchedDurationSeconds > 0) ||
		(remainingDurationSeconds && remainingDurationSeconds > 0) ||
		(totalDurationSeconds !== undefined) ||
		(minutes && minutes > 0) ||
		(actualType === "success") ||
		(actualType === "warning") ||
		(actualType === "empty" && ((watchedDurationSeconds || 0) + (remainingDurationSeconds || 0) > 0));

	const getChipColor = () => {
		switch (actualType) {
			case "success":
				return palette.successChip;
			case "warning":
				return palette.orange;
			case "empty":
				return "transparent";
			default:
				return palette.grey15;
		}
	};

	const getChipIcon = () => {
		switch (actualType) {
			case "success":
				return <CheckCircleOutlineIcon sx={{fontSize: 18, color: "black"}} />;
			case "warning":
				return <AutorenewIcon sx={{fontSize: 18, color: "black"}} />;
			default:
				return undefined;
		}
	};

	const getTextColor = () => {
		switch (actualType) {
			case "success":
			case "warning":
				return "black";
			case "empty":
				return "white";
			default:
				return "white";
		}
	};

	const getChipMinWidth = () => {
		switch (actualType) {
			case "warning":
			case "success":
				return "100px";
			default:
				return "72px";
		}
	};

	const getChipBorder = () => {
		switch (actualType) {
			case "empty":
				return `1px solid ${palette.greyBorder}`;
			case "warning":
				return `1px solid ${palette.orange}`;
			default:
				return "none";
		}
	};

	// Only show chip if there's content to display
	if (!hasContent) {
		return <div style={{height: "24px", marginBottom: "8px"}} />;
	}

	return (
		<Chip
			label={getChipLabel()}
			icon={getChipIcon()}
			sx={{
				backgroundColor: getChipColor(),
				color: getTextColor(),
				border: getChipBorder(),
				minWidth: getChipMinWidth(),
				height: "24px",
				borderRadius: "100px",
				fontSize: "12px",
				fontWeight: 600,
				padding: "0 4px",
				"& .MuiChip-label": {
					color: getTextColor(),
					padding: "0 4px"
				},
				"& .MuiChip-icon": {
					color: getTextColor(),
					marginLeft: "4px",
					marginRight: "2px"
				}
			}}
		/>
	);
};

export default CourseChip;

